import {
  paletteHelper,
  spacingHelper,
  mediaHelper,
  boxShadow,
  radiusHelper,
} from '@property.com.au/ui-kit';

import type { FunctionComponent } from 'react';
import { memo } from 'react';
import styled, { css } from 'styled-components';
import { InternalLink } from '../../../app-kit/components/InternalLink/InternalLink';
import { OutboundLink } from '../../../app-kit/components/OutboundLink/OutboundLink';
import { SearchByImageIconMd } from '../../../app-kit/components/SearchByImageIcon';
import { SearchResult } from '../../types';
import { SearchLandingPagePropertyTile } from '../SearchLandingPagePropertyTile/SearchLandingPagePropertyTile';
import propertyGroupLogoOnly from '../../../assets/rea-group-logo-only.svg';
import { mapSearchResultToPropertyTileProps } from '../../helpers/mapSearchLandnigResultToPropertyTileProps';
import { mapApiWeblinkVariantToLinkVariant } from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';

export interface PropertyCardProps {
  content: SearchResult;
  preloadImage?: boolean;
  className?: string;
}

const tileHoverShadow = css`
  &::after {
    content: '';
    position: absolute;
    z-index: 0;

    width: 95%;
    left: 2.5%;
    height: 26px;
    bottom: 0;

    background: ${paletteHelper('backgroundHoveredSecondary')};
    border-radius: ${radiusHelper('small')};
    filter: blur(4.5px);
    opacity: 0;

    transition: opacity 0.15s ease-in-out;
  }

  &:hover {
    &::after {
      opacity: 1;
    }
  }
`;

const TileWrapper = styled.div`
  width: 49.5%;
  margin-bottom: ${spacingHelper('extraSmall')};
  position: relative;

  ${tileHoverShadow}

  ${mediaHelper('mobile')} {
    width: 100%;
  }
`;

const StyledPropertyTile = styled(SearchLandingPagePropertyTile)`
  ${boxShadow.defaultBoxShadow};
  background: ${paletteHelper('backgroundPrimaryHigh')};
  position: relative;
  z-index: 1;
  padding: 0;
  height: 100%;

  img {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  [class*='PropertyImageWrapper']::after {
    border-radius: 0;
  }

  [class*='Description'] {
    padding-left: ${spacingHelper('small')};
    padding-right: 60px;
    padding-bottom: ${spacingHelper('small')};
  }
`;

const REAGroupLink = styled(OutboundLink)`
  position: absolute;
  z-index: 1;
  bottom: 0;
  right: 0;
  padding: ${spacingHelper('medium')};
`;

const SearchByImageButton = styled(InternalLink)`
  /* Using internal link instead of weblink here to enable the hardcoded icon */
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;

  svg + span {
    margin-left: ${spacingHelper('extraSmall')};
  }
`;

const PropertyCard: FunctionComponent<PropertyCardProps> = ({ content, preloadImage }) => {
  const { reaLinkV2, searchByImageButton } = content;

  return (
    <TileWrapper className="search-page-property-card">
      <StyledPropertyTile
        titleAs="h2"
        preloadImage={preloadImage}
        {...mapSearchResultToPropertyTileProps({ result: content })}
      />
      {reaLinkV2 && (
        <REAGroupLink href={reaLinkV2} outboundLinkType="srp-rea-link">
          <img
            src={propertyGroupLogoOnly}
            alt="View this listing on realestate.com.au"
            width={24}
            height={24}
          />
        </REAGroupLink>
      )}

      {searchByImageButton && (
        <SearchByImageButton
          key={searchByImageButton.url}
          to={searchByImageButton.url}
          linkVariant={mapApiWeblinkVariantToLinkVariant(searchByImageButton.variant)}
          clickThroughTracking={searchByImageButton?.tracking?.clickThroughTracking}
          rel={searchByImageButton?.rel || undefined}
          target="_blank"
          className="searchByImageBtn"
        >
          <>
            <SearchByImageIconMd />
            <span>{searchByImageButton.title}</span>
          </>
        </SearchByImageButton>
      )}
    </TileWrapper>
  );
};

export const SearchLandingPagePropertyCard = memo(
  PropertyCard,
  (prev, current) => prev.content.address === current.content.address,
);
